import React, { Dispatch, SetStateAction, useEffect, useState } from 'react';

import { StateLanguage } from '../../languages/config/StateLanguage';
import { PosCashier } from '../../models/pos.model';
import { GlobalSwal } from '../../styles/swal.style';
import { TreasuryAccount } from '../../models/treasury.account.model';
import { Error } from '../../models/error.model';
import { PosCashierDA } from '../../services/admin/pos.cashier.service';
import { StorageCenter } from '../../models/storage.center.model';
import { Company } from '../../models/company.model';
import { legendIconInvalid, legendIconValidInvalidRestart, legendInvalid, legendValidInvalidRestart } from '../../utils/legend.data.util';
import { modalHide } from '../../utils/modal.util';
import { getError } from '../../languages/translations/response';
import { errorWebHandler } from '../../scripts/error.web.handler.script';
import { formatContact } from '../../scripts/format.value.script';

import AppLoading from '../Loading';
import AppPosCashierForm from '../../interfaces/main/pos/component/PosCashierForm';

export interface AppModalPosCashierUpdateProps {
  companyForUser: Company | null,
  treasuryAccountsForCompany: TreasuryAccount[] | null,
  storageCentersForCompany: StorageCenter[] | null,
  posCashier: PosCashier | null,
  posCashiers: PosCashier[] | null,
  setPosCashier: Dispatch<SetStateAction<PosCashier | null>>,
  setPosCashiers: Dispatch<SetStateAction<PosCashier[] | null>>
};

let errorResponse: Error, posCashierResponse: PosCashier;

const AppModalPosCashierUpdate: React.FunctionComponent<AppModalPosCashierUpdateProps> = ({companyForUser, treasuryAccountsForCompany, storageCentersForCompany, posCashier, posCashiers, setPosCashier, setPosCashiers}) => {
  const {lang} = StateLanguage()

  const [loadIndicator, setLoadIndicator] = useState('off')
  const [name, setName] = useState({value: '', valid: false})
  const [code, setCode] = useState({value: '', valid: false})
  const [stockEnabled, setStockEnabled] = useState({value: true, valid: true})
  const [storageCenter, setStorageCenter] = useState({value: '', valid: false, object: null as any})
  const [treasuryCashAccounts, setTreasuryCashAccounts] = useState({value: [] as string[], valid: false})
  const [treasuryCard, setTreasuryCard] = useState({value: '', valid: false, object: null as any})
  const [defaultCashCurrency, setDefaultCashCurrency] = useState({value: 'CHF', valid: true})
  const [contact, setContact] = useState({value: '', valid: false, object: null as any})

  const submitPosCashierUpdate = async () => {
    setLoadIndicator('on')

    if (companyForUser && posCashiers && posCashier && name.valid && code.valid && (!stockEnabled.value || storageCenter.valid) && treasuryCashAccounts.valid && treasuryCard.valid && contact.valid) {
      await PosCashierDA.putCashier(companyForUser.id, posCashier.id, name.value, code.value, stockEnabled.value, stockEnabled.value ? storageCenter.value : null, treasuryCashAccounts.value, treasuryCard.value, defaultCashCurrency.value, {
        id: contact.value,
        name: formatContact(contact.object),
        type: contact.object?.type ?? 'person',
        uuid: contact.object?.uuid ?? ''
      }).then( (response) => {
        if (response.status === 200) {
          posCashierResponse = response.data
          setPosCashiers(posCashiers.map((temp_item) => (temp_item.id === posCashier.id ? posCashierResponse : temp_item)))

          GlobalSwal.fire({
            title: lang.labels.successfullyUpdatedPos,
            text: lang.labels.actionCompletedReturningToPage,
            icon: 'success',
            showConfirmButton: false,
            timer: 2200
          }).then( () => {
            executeHideModalPosCashierUpdate()
          })
        } else {
          errorResponse = response.data

          GlobalSwal.fire({
            title: getError(errorResponse.code, lang.code, errorResponse.message),
            text: lang.labels.sorryLooksLikeThereAreSomeErrorstryAgain,
            icon: 'error',
            confirmButtonText: lang.labels.okGotIt
          })
        }
      }).catch( (error) => {
        errorWebHandler(lang, error)
      }).finally(() => {
        setLoadIndicator('off')
      })
    } else {
      GlobalSwal.fire({
        text: lang.labels.sorryLooksLikeThereAreSomeErrorsTrySolve,
        icon: 'error',
        showConfirmButton: false,
        timer: 2200
      }).then( () => {
        if (!name.value) legendIconInvalid('modal-pos-cashier-update-input-name', 'modal-pos-cashier-update-legend-name-required')
        if (!code.value) legendIconInvalid('modal-pos-cashier-update-input-code', 'modal-pos-cashier-update-legend-code-required')
        if (stockEnabled.value && !storageCenter.value) legendInvalid('modal-pos-cashier-update-legend-storage-center-required')
        if (treasuryCashAccounts.value.length === 0) legendInvalid('modal-pos-cashier-update-legend-treasury-cash-accounts-required')
        if (!treasuryCard.value) legendInvalid('modal-pos-cashier-update-legend-treasury-account-required-card')
        if (!contact.value) legendInvalid('modal-pos-cashier-update-legend-contact-required')
        setLoadIndicator('off')
      })
    }
  }

  const executeHideModalPosCashierUpdate = () => {
    modalHide('modal-pos-cashier-update')

    setTimeout( () => {
      restartModal()
    }, 200 )
  }

  function restartModal() {
    legendIconValidInvalidRestart('modal-pos-cashier-update-input-name', 'modal-pos-cashier-update-legend-name-valid')
    legendIconValidInvalidRestart('modal-pos-cashier-update-input-code', 'modal-pos-cashier-update-legend-code-required')
    legendValidInvalidRestart('modal-pos-cashier-update-legend-storage-center-required')
    legendValidInvalidRestart('modal-pos-cashier-update-legend-treasury-cash-accounts-required')
    legendValidInvalidRestart('modal-pos-cashier-update-legend-treasury-account-required-card')
    legendValidInvalidRestart('modal-pos-cashier-update-legend-contact-required')

    setPosCashier(null)
  }

  const resolveId = (ref: any): string => {
    if (!ref) return ''
    if (typeof ref === 'string') return ref
    return ref.id ?? ''
  }

  const resolveObject = (ref: any, list: any[] | null): any => {
    if (!ref) return null
    if (typeof ref === 'object' && ref.id) return ref
    if (typeof ref === 'string' && list) return list.find(item => item.id === ref) ?? null
    return null
  }

  useEffect( () => {
    if (posCashier) {
      const storageCenterRef = (posCashier as any).storage_center ?? (posCashier as any).id_storage_center
      const storageCenterObj = resolveObject(storageCenterRef, storageCentersForCompany ?? null)
      const storageCenterId = resolveId(storageCenterRef)

      const cashAccountRefs = posCashier.treasury_cash_accounts ?? []
      const cashAccountIds = cashAccountRefs.map((a: any) => resolveId(a))

      const treasuryCardRef = posCashier.treasury_card
      const treasuryCardObj = resolveObject(treasuryCardRef, treasuryAccountsForCompany ?? null)
      const treasuryCardId = resolveId(treasuryCardRef)

      const customerRef = (posCashier as any).default_customer ?? (posCashier as any).customer_default ?? (posCashier as any).id_customer
      const customerObj = resolveObject(customerRef, null)
      const customerId = resolveId(customerRef)

      const isStockEnabled = posCashier.stock_enabled !== undefined ? posCashier.stock_enabled : true

      setName({value: posCashier.name, valid: true})
      setCode({value: posCashier.code, valid: true})
      setStockEnabled({value: isStockEnabled, valid: true})
      setStorageCenter({value: storageCenterId, valid: !!storageCenterId, object: storageCenterObj})
      setTreasuryCashAccounts({
        value: cashAccountIds,
        valid: cashAccountIds.length > 0
      })
      setTreasuryCard({value: treasuryCardId, valid: !!treasuryCardId, object: treasuryCardObj})
      setDefaultCashCurrency({value: posCashier.default_cash_currency || 'CHF', valid: true})
      setContact({value: customerId, object: customerObj, valid: !!customerId})
    } else {
      setName({value: '', valid: false})
      setCode({value: '', valid: false})
      setStockEnabled({value: true, valid: true})
      setStorageCenter({value: '', valid: false, object: null})
      setTreasuryCashAccounts({value: [], valid: false})
      setTreasuryCard({value: '', valid: false, object: null})
      setDefaultCashCurrency({value: 'CHF', valid: true})
      setContact({value: '', valid: false, object: null})
    }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [posCashier])

  return (
    <div id="modal-pos-cashier-update" className="modal fade" tabIndex={-1} aria-hidden="true" data-bs-backdrop="static" data-bs-keyboard="false">
      <div className="modal-dialog modal-xl">
        <div className="modal-content">
          <div className="modal-header">
            <h2 className="text-capitalize">{lang.labels.updatePos}</h2>
          </div>
          <div className="modal-body">
            { companyForUser && treasuryAccountsForCompany && storageCentersForCompany && posCashier
              ?
              <AppPosCashierForm component={"modal-pos-cashier-update"} name={name} code={code} stockEnabled={stockEnabled} storageCenter={storageCenter} treasuryCashAccounts={treasuryCashAccounts} treasuryCard={treasuryCard} defaultCashCurrency={defaultCashCurrency} contact={contact} setName={setName} setCode={setCode} setStockEnabled={setStockEnabled} setStorageCenter={setStorageCenter} setTreasuryCashAccounts={setTreasuryCashAccounts} setTreasuryCard={setTreasuryCard} setDefaultCashCurrency={setDefaultCashCurrency} setContact={setContact}></AppPosCashierForm>
              :
              <AppLoading></AppLoading>
            }
          </div>
          <div className="modal-footer flex-center">
            <button className="btn btn-secondary mx-2" type="reset" onClick={executeHideModalPosCashierUpdate}>{lang.labels.discard}</button>
            <button className="btn btn-primary mx-2" type="button" data-kt-indicator={loadIndicator} onClick={submitPosCashierUpdate}>
              <span className="indicator-label">{lang.labels.update}</span>
              <span className="indicator-progress">
                {lang.labels.pleaseWait}
                <span className="spinner-border spinner-border-sm align-middle ms-2"></span>
              </span>
            </button>
          </div>
        </div>
      </div>
    </div>
  )
};

export default AppModalPosCashierUpdate;
